import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import { supabase } from '../../config/supabase'

interface Undertaker {
  id: string
  name: string
  city: string
  phone?: string
  email?: string
  published: boolean
  published_at?: string
  created_at: string
  updated_at: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

export default function ApproveUndertakers() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)
  
  const [pendingUndertakers, setPendingUndertakers] = useState<Undertaker[]>([])
  const [publishedUndertakers, setPublishedUndertakers] = useState<Undertaker[]>([])
  
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    undertaker: Undertaker | null
    action: 'publish' | 'unpublish' | null
  }>({
    open: false,
    undertaker: null,
    action: null,
  })
  const [processing, setProcessing] = useState(false)
  
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    undertaker: Undertaker | null
  }>({
    open: false,
    undertaker: null,
  })
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchUndertakers()
  }, [])

  const fetchUndertakers = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('undertakers')
        .select('id, name, city, phone, email, published, published_at, created_at, updated_at')
        .order('created_at', { ascending: false })

      if (error) throw error

      const pending = (data || []).filter((u: Undertaker) => !u.published)
      const published = (data || []).filter((u: Undertaker) => u.published)

      setPendingUndertakers(pending)
      setPublishedUndertakers(published)
    } catch (err: any) {
      setError(err.message)
      console.error('Error fetching undertakers:', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePublishClick = (undertaker: Undertaker) => {
    setConfirmDialog({
      open: true,
      undertaker,
      action: 'publish',
    })
  }

  const handleUnpublishClick = (undertaker: Undertaker) => {
    setConfirmDialog({
      open: true,
      undertaker,
      action: 'unpublish',
    })
  }

  const handleConfirmAction = async () => {
    if (!confirmDialog.undertaker || !confirmDialog.action) return

    try {
      setProcessing(true)
      setError(null)

      const isPublishing = confirmDialog.action === 'publish'

      const { error } = await supabase
        .from('undertakers')
        .update({
          published: isPublishing,
          published_at: isPublishing ? new Date().toISOString() : null,
        })
        .eq('id', confirmDialog.undertaker.id)

      if (error) throw error

      // Refresh the lists
      await fetchUndertakers()

      setConfirmDialog({ open: false, undertaker: null, action: null })
    } catch (err: any) {
      setError(err.message)
      console.error('Error updating undertaker:', err)
    } finally {
      setProcessing(false)
    }
  }

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, undertaker: null, action: null })
  }

  const handleDeleteClick = (undertaker: Undertaker) => {
    setDeleteDialog({
      open: true,
      undertaker,
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.undertaker) return

    try {
      setDeleting(true)
      setError(null)

      const { error } = await supabase
        .from('undertakers')
        .delete()
        .eq('id', deleteDialog.undertaker.id)

      if (error) throw error

      // Refresh the lists
      await fetchUndertakers()

      setDeleteDialog({ open: false, undertaker: null })
    } catch (err: any) {
      setError(err.message)
      console.error('Error deleting undertaker:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, undertaker: null })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Godkend bedemænd
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Kun publicerede bedemænd vises på hjemmesiden
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            pt: 2,
          }}
        >
          <Tab label={`Afventer godkendelse (${pendingUndertakers.length})`} />
          <Tab label={`Publicerede (${publishedUndertakers.length})`} />
        </Tabs>

        {/* Pending Undertakers */}
        <TabPanel value={activeTab} index={0}>
          {pendingUndertakers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Ingen bedemænd afventer godkendelse
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Telefon</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Oprettet</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 150 }} align="right">Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUndertakers.map((undertaker) => (
                    <TableRow key={undertaker.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{undertaker.name}</TableCell>
                      <TableCell>{undertaker.city}</TableCell>
                      <TableCell>{undertaker.phone || '-'}</TableCell>
                      <TableCell>{undertaker.email || '-'}</TableCell>
                      <TableCell>
                        {new Date(undertaker.created_at).toLocaleDateString('da-DK')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/undertakers/edit/${undertaker.id}`)}
                          title="Se detaljer"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handlePublishClick(undertaker)}
                          title="Godkend og publicer"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(undertaker)}
                          title="Slet bedemand"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Published Undertakers */}
        <TabPanel value={activeTab} index={1}>
          {publishedUndertakers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Ingen publicerede bedemænd endnu
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Telefon</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Publiceret</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 150 }} align="right">Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publishedUndertakers.map((undertaker) => (
                    <TableRow key={undertaker.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{undertaker.name}</TableCell>
                      <TableCell>{undertaker.city}</TableCell>
                      <TableCell>{undertaker.phone || '-'}</TableCell>
                      <TableCell>{undertaker.email || '-'}</TableCell>
                      <TableCell>
                        {undertaker.published_at 
                          ? new Date(undertaker.published_at).toLocaleDateString('da-DK')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/undertakers/edit/${undertaker.id}`)}
                          title="Se detaljer"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnpublishClick(undertaker)}
                          title="Afpublicer"
                        >
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancelAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          {confirmDialog.action === 'publish' ? 'Publicer bedemand?' : 'Afpublicer bedemand?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'publish' ? (
              <>
                Er du sikker på at du vil publicere <strong>{confirmDialog.undertaker?.name}</strong>?
                <br /><br />
                Når den er publiceret, vil den være synlig på hjemmesiden.
              </>
            ) : (
              <>
                Er du sikker på at du vil afpublicere <strong>{confirmDialog.undertaker?.name}</strong>?
                <br /><br />
                Når den er afpubliceret, vil den IKKE være synlig på hjemmesiden.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} disabled={processing}>
            Annuller
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog.action === 'publish' ? 'success' : 'error'}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Behandler...' : confirmDialog.action === 'publish' ? 'Publicer' : 'Afpublicer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Slet bedemand?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Er du sikker på at du vil slette <strong>{deleteDialog.undertaker?.name}</strong>?
            <br /><br />
            <strong>⚠️ Advarsel:</strong> Dette kan ikke fortrydes! Bedemanden vil blive permanent slettet fra databasen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Annuller
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Sletter...' : 'Slet permanent'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

